import React from 'react';
import { useQuery } from 'urql';
import { MetaAdCampaignObjective } from '@frontend/gql/social/graphql';
import { getMetaAdAccountsOnly, getMetaAdCampaignsForAds, getMetaAdSetsForAds } from '../graphql';
import { getOptimizationGoalLabel } from '../../constants/optimization-goals';

interface AdAccount {
  id: string;
  name: string;
}

interface AdCampaign {
  id: string;
  name: string;
  adSets?: AdSet[];
  hasPaidPartnershipAds?: boolean;
}

interface AdSet {
  id: string;
  name: string;
  hasPaidPartnershipAds?: boolean;
  // Track the owning campaign to enable grouping by campaign later
  campaignId?: string;
}

interface AdCampaignObjective {
  id: string;
  name: string;
  objective: MetaAdCampaignObjective;
}

interface AdsFilterFormData {
  adAccounts: AdAccount[];
  adCampaigns: AdCampaign[];
  adSets: AdSet[];
  adCampaignObjectives: AdCampaignObjective[];
  isFetchingAdAccounts: boolean;
  isFetchingCampaigns: boolean;
  isFetchingAdSets: boolean;
  isFetchingAdsData: boolean;
}

// Helper function to convert MetaAdCampaignObjective enum to human-readable names
const getObjectiveDisplayName = (objective: MetaAdCampaignObjective): string => {
  // Use shared optimization goal labels for optimization goals
  if (
    objective in
    [
      'REACH',
      'IMPRESSIONS',
      'AD_RECALL_LIFT',
      'LINK_CLICKS',
      'LANDING_PAGE_VIEWS',
      'POST_ENGAGEMENT',
      'EVENT_RESPONSES',
      'VIDEO_VIEWS',
      'LEAD_GENERATION',
      'OFFSITE_CONVERSIONS',
      'APP_INSTALLS',
    ]
  ) {
    return getOptimizationGoalLabel(objective);
  }

  // Handle campaign objectives and other special cases (excluding optimization goals)
  const objectiveNames: Partial<Record<MetaAdCampaignObjective, string>> = {
    BRAND_AWARENESS: 'Brand Awareness',
    CONVERSIONS: 'Conversions',
    LOCAL_AWARENESS: 'Local Awareness',
    MESSAGES: 'Messages',
    MOBILE_APP_ENGAGEMENT: 'Mobile App Engagement',
    MOBILE_APP_INSTALLS: 'Mobile App Installs',
    NONE: 'None',
    OFFER_CLAIMS: 'Offer Claims',
    OUTCOME_APP_PROMOTION: 'App Promotion',
    OUTCOME_AWARENESS: 'Awareness',
    OUTCOME_ENGAGEMENT: 'Engagement',
    OUTCOME_LEADS: 'Leads',
    OUTCOME_SALES: 'Sales',
    OUTCOME_TRAFFIC: 'Traffic',
    PAGE_LIKES: 'Page Likes',
    PRODUCT_CATALOG_SALES: 'Product Catalog Sales',
    STORE_VISITS: 'Store Visits',
  };

  return objectiveNames[objective] || objective;
};

// Type definitions for GraphQL response data
interface MetaAdCreative {
  instagramBrandedContent?: unknown;
}

interface MetaAd {
  metaAdMetaAdCreatives?: MetaAdCreative[];
}

interface MetaAdSetData {
  id?: string;
  name?: string;
  metaAds?: MetaAd[];
}

// Helper function to check if an ad has paid partnership content
const adHasPaidPartnership = (ad: MetaAd): boolean => {
  if (!ad?.metaAdMetaAdCreatives) {
    return false;
  }

  return ad.metaAdMetaAdCreatives.some((creative) => {
    // Check if instagramBrandedContent exists and is not null
    return creative?.instagramBrandedContent != null;
  });
};

// Helper function to check if an ad set has paid partnership ads
const adSetHasPaidPartnershipAds = (adSet: MetaAdSetData): boolean => {
  if (!adSet?.metaAds) {
    return false;
  }

  return adSet.metaAds.some(adHasPaidPartnership);
};

// Removed optimized processors

export const useAdsFilterFormData = (
  clientId?: string,
  selectedAdAccountIds?: string[],
  selectedCampaignIds?: string[],
): AdsFilterFormData => {
  // Stage 1: Query for ad accounts only
  const [{ data: adAccountsData, fetching: isFetchingAdAccounts, error: adAccountsError }] = useQuery({
    query: getMetaAdAccountsOnly,
    variables: {
      clientId: clientId || '',
      accountStatuses: ['ACTIVE', 'ANY_ACTIVE'],
    },
    requestPolicy: 'cache-first',
    pause: !clientId,
  });

  // Stage 2: Query for campaigns filtered by selected ad accounts
  const [{ data: campaignsData, fetching: isFetchingCampaigns, error: campaignsError }] = useQuery({
    query: getMetaAdCampaignsForAds,
    variables: {
      clientId: clientId || '',
      accountIds: selectedAdAccountIds || [],
      campaignStatuses: ['ACTIVE', 'PAUSED'],
    },
    requestPolicy: 'cache-first',
    pause: !clientId || !selectedAdAccountIds || selectedAdAccountIds.length === 0,
  });

  // Stage 3: Query for ad sets filtered by selected campaigns
  const [{ data: adSetsData, fetching: isFetchingAdSets, error: adSetsError }] = useQuery({
    query: getMetaAdSetsForAds,
    variables: {
      clientId: clientId || '',
      accountIds: selectedAdAccountIds || [],
      campaignIds: selectedCampaignIds || [],
      adSetStatuses: ['ACTIVE', 'PAUSED'],
      adStatuses: ['ACTIVE', 'PAUSED', 'PREAPPROVED'],
    },
    requestPolicy: 'cache-first',
    pause:
      !clientId ||
      !selectedAdAccountIds ||
      selectedAdAccountIds.length === 0 ||
      !selectedCampaignIds ||
      selectedCampaignIds.length === 0,
  });

  // Extract ad accounts from the new query
  const metaAdAccounts = React.useMemo(() => {
    return (
      adAccountsData?.client?.metaAdAccounts?.filter((account): account is NonNullable<typeof account> => {
        return !!account;
      }) || []
    );
  }, [adAccountsData?.client?.metaAdAccounts]);

  // Extract campaigns from the new filtered query
  const metaAdCampaigns = React.useMemo(() => {
    if (!campaignsData?.client?.metaAdAccounts) {
      return [];
    }

    const campaigns: AdCampaign[] = [];
    campaignsData.client.metaAdAccounts.forEach((account) => {
      if (!account?.metaAdCampaigns) {
        return;
      }

      account.metaAdCampaigns.forEach((campaign) => {
        if (!campaign?.id || !campaign?.name) {
          return;
        }

        // Since we no longer fetch ad sets in this query, we'll provide empty arrays
        // The ad sets will be fetched separately in the ad sets query
        campaigns.push({
          id: campaign.id,
          name: campaign.name,
          adSets: [], // Will be populated by separate ad sets query
          hasPaidPartnershipAds: false, // Will be determined by ad sets query
        });
      });
    });

    return campaigns;
  }, [campaignsData?.client?.metaAdAccounts]);

  // Extract ad sets from the new filtered query
  const metaAdSets = React.useMemo(() => {
    if (!adSetsData?.client?.metaAdAccounts) {
      return [];
    }

    const adSets: AdSet[] = [];
    adSetsData.client.metaAdAccounts.forEach((account) => {
      if (!account?.metaAdCampaigns) {
        return;
      }

      account.metaAdCampaigns.forEach((campaign) => {
        if (!campaign?.metaAdSets) {
          return;
        }

        campaign.metaAdSets.forEach((adSet) => {
          if (!adSet?.id || !adSet?.name) {
            return;
          }

          const hasPaidPartnershipAds = adSetHasPaidPartnershipAds(adSet);

          adSets.push({
            id: adSet.id,
            name: adSet.name,
            hasPaidPartnershipAds,
            // Persist the campaignId so we can group ad sets by campaign later
            campaignId: campaign.id || undefined,
          });
        });
      });
    });

    return adSets;
  }, [adSetsData]);

  // Merge campaigns with their ad sets data
  const metaAdCampaignsWithAdSets = React.useMemo(() => {
    if (!metaAdCampaigns.length || !metaAdSets.length) {
      return metaAdCampaigns;
    }

    // Build a map of campaignId -> { adSets: AdSet[]; hasPaidPartnership: boolean }
    const campaignAdSetsMap = new Map<string, { adSets: AdSet[]; hasPaidPartnership: boolean }>();

    metaAdSets.forEach((adSet) => {
      const campaignId = adSet.campaignId;
      if (!campaignId) return;

      const existing = campaignAdSetsMap.get(campaignId) || { adSets: [], hasPaidPartnership: false };
      existing.adSets.push(adSet);
      if (adSet.hasPaidPartnershipAds) {
        existing.hasPaidPartnership = true;
      }
      campaignAdSetsMap.set(campaignId, existing);
    });

    // Attach grouped ad sets and per-campaign partnership flag to each campaign
    return metaAdCampaigns.map((campaign) => {
      const entry = campaignAdSetsMap.get(campaign.id);
      return {
        ...campaign,
        adSets: entry?.adSets || [],
        hasPaidPartnershipAds: entry?.hasPaidPartnership || false,
      };
    });
  }, [metaAdCampaigns, metaAdSets]);

  // Note: Waterfall logic is now handled in the form component based on form field values

  // Log errors for debugging
  React.useEffect(() => {
    if (adAccountsError) {
      console.error('❌ Ad Accounts Error:', adAccountsError);
    }
    if (campaignsError) {
      console.error('❌ Campaigns Error:', campaignsError);
    }
    if (adSetsError) {
      console.error('❌ Ad Sets Error:', adSetsError);
    }
  }, [adAccountsError, campaignsError, adSetsError]);

  const mappedAdAccounts = React.useMemo(() => {
    return metaAdAccounts
      .map((account) => ({
        id: account.id || '',
        name: account.name || '',
      }))
      .filter((account) => account.id && account.name);
  }, [metaAdAccounts]);

  const mappedAdCampaigns = React.useMemo(() => {
    // Use the merged campaigns data that includes ad sets
    return metaAdCampaignsWithAdSets;
  }, [metaAdCampaignsWithAdSets]);

  const effectiveAdCampaigns = React.useMemo(() => {
    // Use the merged campaigns data instead of the old joined data
    return mappedAdCampaigns;
  }, [mappedAdCampaigns]);

  // Note: mappedAdSets is no longer needed since we use metaAdSets directly from the Ad Sets query

  // Static list of the 6 OUTCOME_ objectives
  const mappedAdCampaignObjectives = React.useMemo(() => {
    const outcomeObjectives: MetaAdCampaignObjective[] = [
      'OUTCOME_APP_PROMOTION',
      'OUTCOME_AWARENESS',
      'OUTCOME_ENGAGEMENT',
      'OUTCOME_LEADS',
      'OUTCOME_SALES',
      'OUTCOME_TRAFFIC',
    ];

    return outcomeObjectives.map((objective) => ({
      id: objective,
      name: getObjectiveDisplayName(objective),
      objective,
    }));
  }, []);

  return {
    adAccounts: mappedAdAccounts,
    adCampaigns: effectiveAdCampaigns,
    adSets: metaAdSets, // Use the new filtered ad sets data
    adCampaignObjectives: mappedAdCampaignObjectives,
    isFetchingAdAccounts,
    isFetchingCampaigns,
    isFetchingAdSets,
    isFetchingAdsData: isFetchingAdAccounts || isFetchingCampaigns || isFetchingAdSets,
  };
};
